import { LegalLayout, LegalSection } from "@/components/LegalLayout";
import { usePageMeta } from "@/hooks/usePageMeta";
import { BUSINESS } from "@/lib/constants";

export default function PrivacyPolicy() {
  usePageMeta(
    "Privacy Policy | J11 Consulting LLC",
    "Privacy Policy for J11 Consulting LLC. Learn how we collect, use, and protect your information, including mobile information and SMS consent."
  );

  return (
    <LegalLayout
      title="Privacy Policy"
      subtitle={`Last updated: July 2026. This Privacy Policy describes how ${BUSINESS.name} collects, uses, and protects your information.`}
      testId="privacy-policy-page"
    >
      <LegalSection id="privacy-introduction" title="1. Introduction">
        <p>
          {BUSINESS.name} ("we," "our," or "us") respects your privacy. This Privacy Policy explains
          how we collect, use, disclose, and safeguard information when you visit our website, request
          a consultation, or communicate with us about our credit education, financial literacy, and
          funding readiness mentorship services.
        </p>
      </LegalSection>

      <LegalSection id="privacy-information-collected" title="2. Information We Collect">
        <p>We may collect the following categories of information:</p>
        <ul className="list-disc space-y-2 pl-6">
          <li>
            <span className="font-semibold text-[#F2EEE3]">Information you voluntarily provide</span>,
            including your name, email address, phone number, consultation information, and details
            you share through form submissions about what you would like help understanding.
          </li>
          <li>
            <span className="font-semibold text-[#F2EEE3]">Communications with J11</span>, including
            emails, phone calls, and other correspondence related to your inquiries or consultations.
          </li>
          <li>
            <span className="font-semibold text-[#F2EEE3]">SMS consent information</span>, including
            whether you opted in to receive text messages, the date and time of your consent, and the
            page or form where consent was provided.
          </li>
          <li>
            <span className="font-semibold text-[#F2EEE3]">Website usage information</span> that may be
            collected automatically when you visit our website, such as your IP address, browser type,
            device information, and pages visited.
          </li>
        </ul>
      </LegalSection>

      <LegalSection id="privacy-how-we-use" title="3. How We Use Your Information">
        <p>We may use the information we collect to:</p>
        <ul className="list-disc space-y-2 pl-6">
          <li>Provide the services you request, including consultations and mentorship.</li>
          <li>Respond to your inquiries and manage consultation scheduling.</li>
          <li>Provide educational resources you request.</li>
          <li>Provide customer support and communicate about your relationship with us.</li>
          <li>Operate, maintain, and improve our website.</li>
          <li>Meet administrative, security, fraud-prevention, and legally required purposes.</li>
        </ul>
      </LegalSection>

      <LegalSection id="privacy-cookies" title="4. Cookies & Tracking Technologies">
        <p>
          {BUSINESS.name} may use cookies and similar tracking technologies to help operate the
          website, understand website traffic, remember user preferences, improve website
          performance, and measure how visitors interact with our pages.
        </p>
        <p>
          These technologies may collect information such as browser type, device information, IP
          address, pages visited, referring pages, approximate location, and interaction data.
        </p>
        <p>
          We may use analytics or service providers that process this information on our behalf
          solely for website operations, analytics, security, performance, and related business
          purposes.
        </p>
        <p>
          Users may control or disable cookies through their browser settings. Disabling certain
          cookies may affect how parts of the website function.
        </p>
      </LegalSection>

      <LegalSection id="privacy-service-providers" title="5. Service Providers">
        <p>
          We may share information with trusted service providers that help us operate our business
          and website, such as website hosting, communications, and scheduling providers. These
          service providers may access information only as necessary to perform services on our
          behalf, are not granted independent rights to use your information for their own marketing,
          and are subject to applicable confidentiality and data-protection requirements.
        </p>
      </LegalSection>

      <LegalSection id="privacy-sms-mobile" title="6. Mobile Information & SMS Consent">
        <p className="font-semibold text-[#F2EEE3]">
          Mobile phone numbers, SMS opt-in data and SMS consent information will not be sold, rented
          or shared with third parties or affiliates for their marketing or promotional purposes.
        </p>
        <p>
          If service providers need limited access to mobile information, that access is granted
          solely to deliver communications or operate {BUSINESS.name} systems on our behalf and does
          not give those providers independent marketing rights.
        </p>
        <p>
          If you voluntarily opt in to SMS messaging: message frequency may vary, and message and data
          rates may apply. You may reply STOP to any message to opt out at any time, or reply HELP for
          assistance. SMS consent is not a condition of purchase.
        </p>
        <p>
          You may contact {BUSINESS.name} at {BUSINESS.phone} with any privacy questions, including
          questions about your mobile information or SMS consent.
        </p>
      </LegalSection>

      <LegalSection id="privacy-your-choices" title="7. Your Privacy Choices">
        <p>
          You may choose not to provide certain information through our website forms, although this
          may limit our ability to respond to your inquiry. You may opt out of SMS communications at
          any time by replying STOP. You may also contact us at any time to ask questions about the
          information you have provided or to request updates or deletion where applicable.
        </p>
      </LegalSection>

      <LegalSection id="privacy-security" title="8. Data Security">
        <p>
          We use reasonable administrative, technical, and physical safeguards designed to protect the
          information we collect. However, no method of transmission over the internet or electronic
          storage is completely secure, and we cannot guarantee absolute security.
        </p>
      </LegalSection>

      <LegalSection id="privacy-changes" title="9. Changes to This Privacy Policy">
        <p>
          We may update this Privacy Policy from time to time. Any changes will be posted on this page
          with an updated revision date. We encourage you to review this page periodically.
        </p>
      </LegalSection>

      <LegalSection id="privacy-contact" title="10. Contact Us">
        <p>If you have questions about this Privacy Policy or our privacy practices, contact us at:</p>
        <p className="font-semibold text-[#F2EEE3]">
          {BUSINESS.name}
          <br />
          195 Malabar Rd, Suite #3
          <br />
          Palm Bay, FL 32907
          <br />
          Phone: {BUSINESS.phone}
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
